// frontend/src/pages/admin/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { QRCodeCanvas } from 'qrcode.react';

ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboard = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(null);
  const [school, setSchool] = useState(null);
  const [error, setError] = useState(null);
  const [showScores, setShowScores] = useState(false);
  const [scores, setScores] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);
  const [qrCodes, setQRCodes] = useState({
    iit: '',
    iitUrl: '',
    eit1: '',
    eit1Url: '',
    eit2: '',
    eit2Url: ''
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [activeQRType, setActiveQRType] = useState(null);

  // คำอธิบายแบบสอบถามแต่ละประเภท
  const questionnaireDescriptions = {
    iit: "สำหรับบุคลากรภายในหน่วยงาน เช่น ผู้บริหาร ครู และบุคลากรทางการศึกษา",
    eit1: "สำหรับผู้มีส่วนได้ส่วนเสียที่โรงเรียนกำหนด เช่น ผู้ปกครอง คณะกรรมการสถานศึกษา",
    eit2: "สำหรับผู้มีส่วนได้ส่วนเสียทั่วไป บุคคลภายนอก ผู้มาติดต่อราชการ"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get active fiscal year
        const yearResponse = await api.get('/fiscal-years/active');
        setFiscalYear(yearResponse.data);

        // Get school info
        const schoolResponse = await api.get(`/schools/${user.school_id}`);
        setSchool(schoolResponse.data);

        if (yearResponse.data && yearResponse.data.id) {
          // Fetch dashboard data
          const dashboardResponse = await api.get(`/dashboard/${user.school_id}/${yearResponse.data.id}`);
          setDashboardData(dashboardResponse.data);
          
          // Set system settings
          setSystemSettings(dashboardResponse.data.settings);
          
          // Check if scores should be shown
          setShowScores(dashboardResponse.data.settings?.show_scores || false);

          // Generate QR codes
          generatePublicQRCodes(yearResponse.data.id, user.school_id);

          // Try to fetch scores only if needed
          if (dashboardResponse.data.settings?.show_scores) {
            try {
              const scoresResponse = await api.get(`/reports/total/${user.school_id}/${yearResponse.data.id}`);
              setScores(scoresResponse.data);
            } catch (scoreError) {
              console.error('Could not fetch scores:', scoreError);
              setScores(null);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.school_id]);

  // คำนวณสถานะการตอบแบบสอบถาม
  const getCompletionStatus = (current, quota) => {
    if (!quota) return 0;
    const percentage = (current / quota) * 100;
    return Math.min(percentage, 100);
  };

  // ฟังก์ชันสร้าง QR Codes สำหรับหน้าแบบสอบถามสาธารณะ
  const generatePublicQRCodes = (fiscalYearId, schoolId) => {
    const baseUrl = window.location.origin;
    
    const iitUrl = `${baseUrl}/questionnaire/1/${fiscalYearId}/${schoolId}`;
    const eit1Url = `${baseUrl}/questionnaire/2/${fiscalYearId}/${schoolId}`;
    const eit2Url = `${baseUrl}/questionnaire/3/${fiscalYearId}/${schoolId}`;
    
    setQRCodes({
      iitUrl: iitUrl,
      eit1Url: eit1Url,
      eit2Url: eit2Url
    });
  };

  // ฟังก์ชันคัดลอกลิงค์
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'คัดลอกลิงค์แล้ว',
          text: `คัดลอกลิงค์แบบสอบถาม ${type} เรียบร้อยแล้ว`,
          timer: 1500,
          showConfirmButton: false
        });
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถคัดลอกลิงค์ได้'
        });
      });
  };

  // ฟังก์ชันแสดง Modal QR Code พร้อมคำอธิบาย
  const showQRCodeModal = (type) => {
    setActiveQRType(type);
    setShowQRModal(true);
  };

  // ฟังก์ชันตรวจสอบสถานะแบบสอบถามต่างๆ
  const getIITStatus = () => {
    if (!systemSettings) return 'รอการตั้งค่า';
    
    const now = new Date();
    const startDate = new Date(systemSettings.iit_start_date);
    const endDate = new Date(systemSettings.iit_end_date);
    
    if (now < startDate) return 'รอเปิดระบบ';
    if (now > endDate) return 'ปิดระบบแล้ว';
    return 'เปิดให้ตอบแบบสอบถาม';
  };
  
  const getEIT1Status = () => {
    if (!systemSettings) return 'รอการตั้งค่า';
    
    const now = new Date();
    const startDate = new Date(systemSettings.eit1_start_date);
    const endDate = new Date(systemSettings.eit1_end_date);
    
    if (now < startDate) return 'รอเปิดระบบ';
    if (now > endDate) return 'ปิดระบบแล้ว';
    return 'เปิดให้ตอบแบบสอบถาม';
  };
  
  const getEIT2Status = () => {
    if (!systemSettings) return 'รอการตั้งค่า';
    
    const now = new Date();
    const startDate = new Date(systemSettings.eit2_start_date);
    const endDate = new Date(systemSettings.eit2_end_date);
    
    if (now < startDate) return 'รอเปิดระบบ';
    if (now > endDate) return 'ปิดระบบแล้ว';
    return 'เปิดให้ตอบแบบสอบถาม';
  };
  
  const getOITStatus = () => {
    if (!systemSettings) return 'รอการตั้งค่า';
    
    const now = new Date();
    const startDate = new Date(systemSettings.oit_start_date);
    const endDate = new Date(systemSettings.oit_end_date);
    
    if (now < startDate) return 'รอเปิดระบบ';
    if (now > endDate) return 'ปิดระบบแล้ว';
    return 'เปิดให้ส่งข้อมูล';
  };

  // ฟังก์ชันตรวจสอบสถานะการเปิดให้ตอบแบบสอบถาม
  const isQuestionnaireOpen = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return now >= start && now <= end;
  };

  // ฟังก์ชันดาวน์โหลด QR Code
  const downloadQRCode = (type) => {
    const qrCanvas = document.getElementById(`qr-code-${type}`);
    if (!qrCanvas) return;
    
    const qrDataURL = qrCanvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = qrDataURL;
    link.download = `QR_${type}_${school ? school.name : 'school'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="wrapper">
        <Header user={user} title="หน้าหลัก" />
        <Sidebar user={user} />
        <div className="content-wrapper d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">กำลังโหลด...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="wrapper">
        <Header user={user} title="หน้าหลัก" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="alert alert-danger">
                <h5><i className="fas fa-exclamation-triangle me-2"></i>เกิดข้อผิดพลาด</h5>
                <p className="mb-0">{error}</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // สร้างข้อมูลสำหรับกราฟสรุปการตอบแบบสอบถาม
  const responseChartData = {
    labels: ['IIT', 'EIT1', 'EIT2'],
    datasets: [
      {
        label: 'จำนวนผู้ตอบแบบสอบถาม',
        data: dashboardData ? [
          dashboardData.quotas.iit.current,
          dashboardData.quotas.eit1.current,
          dashboardData.quotas.eit2.current
        ] : [0, 0, 0],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="wrapper">
      <Header user={user} title="หน้าหลัก" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">หน้าหลัก</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-end">
                  <li className="breadcrumb-item active">หน้าหลัก</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {/* ปีงบประมาณและโรงเรียน */}
            <div className="row mb-4">
              <div className="col-lg-6">
                <div className="info-box bg-info">
                  <span className="info-box-icon"><i className="fas fa-calendar-alt"></i></span>
                  <div className="info-box-content">
                    <span className="info-box-text">ปีงบประมาณปัจจุบัน</span>
                    <span className="info-box-number h5">
                      {fiscalYear ? fiscalYear.year : 'ไม่มีปีงบประมาณที่เปิดใช้งาน'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="info-box bg-success">
                  <span className="info-box-icon"><i className="fas fa-school"></i></span>
                  <div className="info-box-content">
                    <span className="info-box-text">โรงเรียน</span>
                    <span className="info-box-number h5">
                      {school ? school.name : 'ไม่พบข้อมูลโรงเรียน'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ส่วนแชร์ QR Code และลิงก์แบบสอบถาม (เพิ่มใหม่) */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h3 className="card-title">
                      <i className="fas fa-qrcode me-2"></i> แชร์แบบสอบถามผ่าน QR Code และลิงก์
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>คำแนะนำ:</strong> สามารถแชร์ QR Code หรือลิงก์ให้กับผู้ที่ต้องการตอบแบบสอบถามได้โดยตรง โดยไม่จำเป็นต้องเข้าสู่ระบบ 
                      เหมาะสำหรับแชร์ผ่าน Line, Facebook หรือแปะไว้ที่บอร์ดประชาสัมพันธ์ของโรงเรียน
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-primary text-white text-center">
                            <h5 className="mb-0">แบบสอบถาม IIT</h5>
                            <small>สำหรับบุคลากรภายในหน่วยงาน</small>
                          </div>
                          <div className="card-body text-center">
                            <button 
                              className="btn btn-lg btn-outline-primary mb-3 w-100"
                              onClick={() => showQRCodeModal('iit')}
                              disabled={getIITStatus() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-2"></i> แสดง QR Code
                            </button>
                            <button 
                              className="btn btn-outline-success mb-2 w-100"
                              onClick={() => copyToClipboard(qrCodes.iitUrl, 'IIT')}
                              disabled={getIITStatus() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-copy me-2"></i> คัดลอกลิงค์
                            </button>
                            <a 
                              href={qrCodes.iitUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="btn btn-outline-info w-100"
                              disabled={getIITStatus() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-external-link-alt me-2"></i> เปิดลิงค์
                            </a>
                          </div>
                          <div className="card-footer bg-light">
                            <span className={`badge ${
                              getIITStatus() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getIITStatus() === 'รอเปิดระบบ' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {getIITStatus()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-success text-white text-center">
                            <h5 className="mb-0">แบบสอบถาม EIT1</h5>
                            <small>สำหรับผู้มีส่วนได้ส่วนเสียที่โรงเรียนกำหนด</small>
                          </div>
                          <div className="card-body text-center">
                            <button 
                              className="btn btn-lg btn-outline-primary mb-3 w-100"
                              onClick={() => showQRCodeModal('eit1')}
                              disabled={getEIT1Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-2"></i> แสดง QR Code
                            </button>
                            <button 
                              className="btn btn-outline-success mb-2 w-100"
                              onClick={() => copyToClipboard(qrCodes.eit1Url, 'EIT1')}
                              disabled={getEIT1Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-copy me-2"></i> คัดลอกลิงค์
                            </button>
                            <a 
                              href={qrCodes.eit1Url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="btn btn-outline-info w-100"
                              disabled={getEIT1Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-external-link-alt me-2"></i> เปิดลิงค์
                            </a>
                          </div>
                          <div className="card-footer bg-light">
                            <span className={`badge ${
                              getEIT1Status() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getEIT1Status() === 'รอเปิดระบบ' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {getEIT1Status()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-info text-white text-center">
                            <h5 className="mb-0">แบบสอบถาม EIT2</h5>
                            <small>สำหรับผู้มีส่วนได้ส่วนเสียทั่วไป</small>
                          </div>
                          <div className="card-body text-center">
                            <button 
                              className="btn btn-lg btn-outline-primary mb-3 w-100"
                              onClick={() => showQRCodeModal('eit2')}
                              disabled={getEIT2Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-2"></i> แสดง QR Code
                            </button>
                            <button 
                              className="btn btn-outline-success mb-2 w-100"
                              onClick={() => copyToClipboard(qrCodes.eit2Url, 'EIT2')}
                              disabled={getEIT2Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-copy me-2"></i> คัดลอกลิงค์
                            </button>
                            <a 
                              href={qrCodes.eit2Url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="btn btn-outline-info w-100"
                              disabled={getEIT2Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-external-link-alt me-2"></i> เปิดลิงค์
                            </a>
                          </div>
                          <div className="card-footer bg-light">
                            <span className={`badge ${
                              getEIT2Status() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getEIT2Status() === 'รอเปิดระบบ' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {getEIT2Status()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* สถานะระบบ */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">สถานะระบบ</h3>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ประเภท</th>
                          <th>สถานะ</th>
                          <th>ระยะเวลา</th>
                          <th>การดำเนินการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>แบบสอบถาม IIT</td>
                          <td>
                            <span className={`badge ${
                              getIITStatus() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getIITStatus() === 'รอเปิดระบบ' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {getIITStatus()}
                            </span>
                          </td>
                          <td>
                            {systemSettings ? (
                              <>
                                {new Date(systemSettings.iit_start_date).toLocaleDateString('th-TH')} ถึง {' '}
                                {new Date(systemSettings.iit_end_date).toLocaleDateString('th-TH')}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            <button 
                              className="btn btn-primary btn-sm me-1" 
                              onClick={() => showQRCodeModal('iit')}
                              disabled={getIITStatus() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-1"></i> QR Code
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td>แบบสอบถาม EIT1</td>
                          <td>
                            <span className={`badge ${
                              getEIT1Status() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getEIT1Status() === 'รอเปิดระบบ' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {getEIT1Status()}
                            </span>
                          </td>
                          <td>
                            {systemSettings ? (
                              <>
                                {new Date(systemSettings.eit1_start_date).toLocaleDateString('th-TH')} ถึง {' '}
                                {new Date(systemSettings.eit1_end_date).toLocaleDateString('th-TH')}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            <button 
                              className="btn btn-primary btn-sm me-1" 
                              onClick={() => showQRCodeModal('eit1')}
                              disabled={getEIT1Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-1"></i> QR Code
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td>แบบสอบถาม EIT2</td>
                          <td>
                            <span className={`badge ${
                              getEIT2Status() === 'เปิดให้ตอบแบบสอบถาม' ? 'bg-success' :
                              getEIT2Status() === 'รอเปิดระบบ' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {getEIT2Status()}
                            </span>
                          </td>
                          <td>
                            {systemSettings ? (
                              <>
                                {new Date(systemSettings.eit2_start_date).toLocaleDateString('th-TH')} ถึง {' '}
                                {new Date(systemSettings.eit2_end_date).toLocaleDateString('th-TH')}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            <button 
                              className="btn btn-primary btn-sm me-1" 
                              onClick={() => showQRCodeModal('eit2')}
                              disabled={getEIT2Status() !== 'เปิดให้ตอบแบบสอบถาม'}
                            >
                              <i className="fas fa-qrcode me-1"></i> QR Code
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td>แบบสอบถาม OIT</td>
                          <td>
                            <span className={`badge ${
                              getOITStatus() === 'เปิดให้ส่งข้อมูล' ? 'bg-success' :
                              getOITStatus() === 'รอเปิดระบบ' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {getOITStatus()}
                            </span>
                          </td>
                          <td>
                            {systemSettings ? (
                              <>
                                {new Date(systemSettings.oit_start_date).toLocaleDateString('th-TH')} ถึง {' '}
                                {new Date(systemSettings.oit_end_date).toLocaleDateString('th-TH')}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            <Link 
                              to="/admin/oit" 
                              className={`btn btn-${getOITStatus() === 'เปิดให้ส่งข้อมูล' ? 'success' : 'secondary'} btn-sm`}
                              disabled={getOITStatus() !== 'เปิดให้ส่งข้อมูล'}
                            >
                              <i className="fas fa-link"></i> จัดการข้อมูล OIT
                            </Link>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* แสดงความคืบหน้าการตอบแบบสอบถาม */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h3 className="card-title">ความคืบหน้าการตอบแบบสอบถาม</h3>
                  </div>
                  <div className="card-body">
                    {dashboardData ? (
                      <>
                        <div className="row">
                          <div className="col-md-4">
                            <h5>แบบสอบถาม IIT (บุคลากรภายในหน่วยงาน)</h5>
                            <div className="progress mb-2">
                              <div 
                                className="progress-bar bg-primary" 
                                role="progressbar" 
                                style={{ width: `${getCompletionStatus(dashboardData.quotas.iit.current, dashboardData.quotas.iit.quota)}%` }}
                                aria-valuenow={dashboardData.quotas.iit.current} 
                                aria-valuemin="0" 
                                aria-valuemax={dashboardData.quotas.iit.quota}
                              ></div>
                            </div>
                            <p>
                              {dashboardData.quotas.iit.current} / {dashboardData.quotas.iit.quota} คน
                              ({dashboardData.quotas.iit.quota ? 
                                (getCompletionStatus(dashboardData.quotas.iit.current, dashboardData.quotas.iit.quota)).toFixed(2) : 0}%)
                            </p>
                          </div>
                          <div className="col-md-4">
                            <h5>แบบสอบถาม EIT1 (ผู้มีส่วนได้ส่วนเสียโรงเรียนกำหนด)</h5>
                            <div className="progress mb-2">
                              <div 
                                className="progress-bar bg-info" 
                                role="progressbar" 
                                style={{ width: `${getCompletionStatus(dashboardData.quotas.eit1.current, dashboardData.quotas.eit1.quota)}%` }}
                                aria-valuenow={dashboardData.quotas.eit1.current} 
                                aria-valuemin="0" 
                                aria-valuemax={dashboardData.quotas.eit1.quota}
                              ></div>
                            </div>
                            <p>
                              {dashboardData.quotas.eit1.current} / {dashboardData.quotas.eit1.quota} คน
                              ({dashboardData.quotas.eit1.quota ? 
                                (getCompletionStatus(dashboardData.quotas.eit1.current, dashboardData.quotas.eit1.quota)).toFixed(2) : 0}%)
                            </p>
                          </div>
                          <div className="col-md-4">
                            <h5>แบบสอบถาม EIT2 (ผู้มีส่วนได้ส่วนเสียทั่วไป)</h5>
                            <div className="progress mb-2">
                              <div 
                                className="progress-bar bg-success" 
                                role="progressbar" 
                                style={{ width: `${getCompletionStatus(dashboardData.quotas.eit2.current, dashboardData.quotas.eit2.quota)}%` }}
                                aria-valuenow={dashboardData.quotas.eit2.current} 
                                aria-valuemin="0" 
                                aria-valuemax={dashboardData.quotas.eit2.quota}
                              ></div>
                            </div>
                            <p>
                              {dashboardData.quotas.eit2.current} / {dashboardData.quotas.eit2.quota} คน
                              ({dashboardData.quotas.eit2.quota ? 
                                (getCompletionStatus(dashboardData.quotas.eit2.current, dashboardData.quotas.eit2.quota)).toFixed(2) : 0}%)
                            </p>
                          </div>
                        </div>

                        <div className="row mt-4">
                          <div className="col-md-6">
                            <h5>การส่งข้อมูล OIT (การเปิดเผยข้อมูลสาธารณะ)</h5>
                            <p>
                              <i className="fas fa-link me-2"></i>
                              จำนวนลิงก์ที่ส่งแล้ว: {dashboardData.oitSubmissionCount} รายการ
                            </p>
                          </div>
                          <div className="col-md-6">
                            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
                              <Pie data={responseChartData} />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        ไม่พบข้อมูลความคืบหน้าการตอบแบบสอบถาม
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* แสดงคะแนนเฉพาะเมื่อ showScores เป็น true */}
            {showScores && scores && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-success text-white">
                      <h3 className="card-title">ผลการประเมิน ITA</h3>
                    </div>
                    <div className="card-body">
                      <div className="row text-center">
                        <div className="col-md-3">
                          <div className="info-box bg-primary">
                            <div className="info-box-content p-3">
                              <span className="info-box-text">คะแนน IIT</span>
                              <span className="info-box-number h3">{scores.iit_score.toFixed(2)}</span>
                              <span className="info-box-text">คะแนนเต็ม 30</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="info-box bg-info">
                            <div className="info-box-content p-3">
                              <span className="info-box-text">คะแนน EIT</span>
                              <span className="info-box-number h3">{scores.eit_score.toFixed(2)}</span>
                              <span className="info-box-text">คะแนนเต็ม 30</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="info-box bg-warning">
                            <div className="info-box-content p-3">
                              <span className="info-box-text">คะแนน OIT</span>
                              <span className="info-box-number h3">{scores.oit_score.toFixed(2)}</span>
                              <span className="info-box-text">คะแนนเต็ม 40</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="info-box bg-success">
                            <div className="info-box-content p-3">
                              <span className="info-box-text">คะแนนรวม</span>
                              <span className="info-box-number h3">{scores.total_score.toFixed(2)}</span>
                              <span className="info-box-text">คะแนนเต็ม 100</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-12 text-center">
                          <h4>ระดับผลการประเมิน</h4>
                          <div className="mt-2">
                            <span className={`badge fs-5 ${
                              scores.rating === 'ผ่านดีเยี่ยม' ? 'bg-success' :
                              scores.rating === 'ผ่านดี' ? 'bg-primary' :
                              scores.rating === 'ผ่าน' ? 'bg-info' :
                              scores.rating === 'ต้องปรับปรุง' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {scores.rating}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ปุ่มจัดการแบบสอบถาม */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-primary text-white">
                    <h3 className="card-title">จัดการข้อมูลแบบสอบถาม</h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <Link to="/admin/respondents" className="btn btn-outline-primary btn-lg w-100">
                          <i className="fas fa-users me-2"></i>จัดการผู้ตอบแบบสอบถาม
                        </Link>
                      </div>
                      <div className="col-md-6 mb-3">
                        <Link to="/admin/oit" className="btn btn-outline-success btn-lg w-100">
                          <i className="fas fa-link me-2"></i>จัดการข้อมูล OIT
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-info text-white">
                    <h3 className="card-title">ข้อมูลอื่นๆ</h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <Link to="/profile" className="btn btn-outline-info btn-lg w-100">
                          <i className="fas fa-user-cog me-2"></i>จัดการข้อมูลส่วนตัว
                        </Link>
                      </div>
                      <div className="col-md-6 mb-3">
                        {showScores ? (
                          <button className="btn btn-outline-secondary btn-lg w-100" onClick={() => window.print()}>
                            <i className="fas fa-print me-2"></i>พิมพ์รายงาน
                          </button>
                        ) : (
                          <button className="btn btn-outline-secondary btn-lg w-100" disabled>
                            <i className="fas fa-print me-2"></i>พิมพ์รายงาน
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Modal สำหรับแสดง QR Code */}
      {showQRModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowQRModal(false)}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className={`modal-header ${
                activeQRType === 'iit' ? 'bg-primary' :
                activeQRType === 'eit1' ? 'bg-success' : 'bg-info'
              } text-white`}>
                <h5 className="modal-title">
                  <i className="fas fa-qrcode me-2"></i>
                  QR Code สำหรับแบบสอบถาม {activeQRType?.toUpperCase()}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
              </div>
              <div className="modal-body text-center">
                <div className="alert alert-info">
                  <strong>คำแนะนำ:</strong> {questionnaireDescriptions[activeQRType]}
                </div>
                <div className="mb-4">
                <QRCodeCanvas
                    id={`qr-code-${activeQRType}`}
                    value={qrCodes[`${activeQRType}Url`]} 
                    size={250} 
                    renderAs="canvas"
                    includeMargin={true}
                    level="H"
                  />
                </div>
                <div className="input-group mb-3">
                  <input 
                    type="text" 
                    className="form-control" 
                    value={qrCodes[`${activeQRType}Url`]} 
                    readOnly 
                  />
                  <button 
                    className="btn btn-outline-primary" 
                    type="button"
                    onClick={() => copyToClipboard(qrCodes[`${activeQRType}Url`], activeQRType?.toUpperCase())}
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-primary" 
                  onClick={() => downloadQRCode(activeQRType)}
                >
                  <i className="fas fa-download me-2"></i>ดาวน์โหลด QR Code
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowQRModal(false)}
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;